import useMinimumLoading from "../../hooks/useMinimumLoading";
import GroupSkeleton from "../../components/loading/GroupSkeleton";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {motion} from "motion/react";
import {useNavigate, useParams} from "react-router-dom";
import {GroupSessionService} from "../../services/GroupSessionService";
import {GroupSessionResponse} from "../../models/GroupSessionResponse";
import {getCurrentUserId} from "../../utils/auth_utils";

import {DecorationUnderline} from "../../assets/svgComponents/inviteFriendsPage/DecorationUnderline";
import CloudLeft from "../../assets/images/FullCloud.png";
import CloudRight from "../../assets/images/FullCloud.png";
import {SearchIcon} from "../../assets/svgComponents/searchIcon";
import {FarewiseIconLight} from "../../assets/svgComponents/FarewiseIconLight";
import {InjectedAuthProps} from "../../auth/RestrictedAccess";

type Participant = {
    userId?: string;
    email?: string;
    displayName?: string;
    status: "INVITED" | "JOINED" | "SUBMITTED";
};

export default function SessionWaitingPage({ username }: InjectedAuthProps) {
    const {sessionId} = useParams<{ sessionId: string }>();
    const navigate = useNavigate();

    const [session, setSession] = useState<GroupSessionResponse | null>(null);
    const [participants, setParticipants] = useState<Participant[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [computing, setComputing] = useState(false);
    const computeInFlight = useRef(false);
    const [copied, setCopied] = useState(false);

    const currentUserId = getCurrentUserId();

    // Load session + participants
    useEffect(() => {
        let active = true;
        let timer: ReturnType<typeof setTimeout>;
        const load = async () => {
            if (!sessionId) return;
            try {

                const [s, status] = await Promise.all([
                    GroupSessionService.getSession(sessionId),
                    GroupSessionService.getSessionStatus(sessionId),
                ]);
                if (!active) return;
                setError(null);
                setSession(s);
                setParticipants(status);
            } catch (e: any) {
                if (active) setError(e?.message || "Failed to load session.");
            } finally {
                if (active) {
                    setLoading(false);
                    timer = setTimeout(load, 5000);
                }
            }
        };
        load();
        return () => { active = false; clearTimeout(timer); };
    }, [sessionId]);

    // Host + current participant
    const isHost = useMemo(
        () => !!session && session.creatorUserId === currentUserId,
        [session, currentUserId]
    );

    const me = useMemo(
        () => participants.find((p) => p.userId === currentUserId),
        [participants, currentUserId]
    );

    // All submitted → host can start search
    const allSubmitted = useMemo(
        () => participants.length > 0 && participants.every((p) => p.status === "SUBMITTED"),
        [participants]
    );

    // Primary CTA logic
    const {ctaLabel, ctaDisabled, ctaOnClick} = useMemo(() => {
        if (computing) return {ctaLabel: "Searching…", ctaDisabled: true, ctaOnClick: undefined};
        if (session?.status === "COMPLETED") return {
            ctaLabel: "View results", ctaDisabled: false,
            ctaOnClick: () => navigate(`/sessions/${sessionId}/unveil`),
        };
        if (isHost && allSubmitted) {
            return {
                ctaLabel: "Start the search",
                ctaDisabled: false,
                ctaOnClick: async () => {
                    if (!sessionId || computeInFlight.current) return;
                    computeInFlight.current = true;
                    setComputing(true);
                    setError(null);
                    try {
                        await GroupSessionService.computeResults(sessionId);
                        navigate(`/sessions/${sessionId}/unveil`);
                    } catch {
                        setError("Search failed. Please try again once everyone has submitted.");
                    } finally {
                        computeInFlight.current = false;
                        setComputing(false);
                    }
                },
            };
        }

        if (me?.status === "JOINED") {
            return {
                ctaLabel: "Submit Preferences",
                ctaDisabled: false,
                ctaOnClick: () => navigate(`/sessions/${sessionId}/preferences`),
            };
        }

        if (me?.status === "SUBMITTED") {
            return {ctaLabel: "Waiting for Friends", ctaDisabled: true, ctaOnClick: undefined};
        }

        return {ctaLabel: "Waiting for Friends", ctaDisabled: true, ctaOnClick: undefined};
    }, [isHost, allSubmitted, me?.status, navigate, sessionId, computing, session?.status]);

    // Delete session (host only)
    const handleDeleteSessionHost = async () => {
        if (!isHost || !sessionId) return;
        const ok = window.confirm("Delete this session for everyone? This cannot be undone.");
        if (!ok) return;
        try {
            await GroupSessionService.deleteSession(sessionId);
            navigate("/sessions");
        } catch (e: any) {
            setError(e?.message || "Failed to delete session.");
        }
    };

    // Share link + copy feedback
    const shareUrl = useMemo(() => `${window.location.origin}/sessions/${sessionId}/join`, [sessionId]);
    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch (e: any) {
            setError(e?.message || "Failed to copy link.");
        }
    };

    // Group participants for UI
    const submittedList = useMemo(
        () => participants.filter((p) => p.status === "SUBMITTED"),
        [participants]
    );
    const notFilledList = useMemo(
        () => participants.filter((p) => p.status !== "SUBMITTED"), // JOINED + INVITED
        [participants]
    );

    // Chip styling by status (INVITED more transparent)
    const chipClasses = (st: Participant["status"]) => {
        const base =
            "flex items-center gap-2 rounded-[10px] px-4 py-2 shadow-sm backdrop-blur text-[14px] font-medium";
        switch (st) {
            case "SUBMITTED":
                return `${base} bg-white/70 text-[#232323]`;
            case "JOINED":
                return `${base} bg-white/60 text-[#232323]`;
            case "INVITED":
                return `${base} bg-white/40 text-[#232323]`;
            default:
                return `${base} bg-white/60 text-[#232323]`;
        }
    };

    const showLoading = useMinimumLoading(loading || computing);

    return (
        <motion.div
            className="relative h-screen w-full bg-gradient-to-b from-[#f6f8ff] from-[43.912%] to-[#dde5ff]"
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            transition={{duration: 0.25, ease: "easeOut"}}
        >
            {/* Back to sessions */}
            <button
                type="button"
                aria-label="Back"
                className="absolute left-28 top-20 z-50 hover:scale-105"
                onClick={() => navigate(`/sessions`)}
            >
                <FarewiseIconLight/>
            </button>

            {/* Top-right controls: Delete (host only) + Copy Link */}
            <div className="absolute right-16 top-20 z-50 flex items-center gap-3">
                {!isHost && me && session?.status !== "COMPLETED" && <button type="button"
                    className="rounded-full bg-white/80 px-4 py-2 text-sm font-semibold"
                    onClick={async () => {
                        try { await GroupSessionService.leave(sessionId!); navigate("/sessions"); }
                        catch { setError("Could not leave the session. Please try again."); }
                    }}>Leave session</button>}
                {me?.status === "SUBMITTED" && !computing && <button type="button"
                    className="rounded-full bg-white/80 px-4 py-2 text-sm font-semibold"
                    onClick={() => navigate(`/sessions/${sessionId}/preferences`)}>Edit preferences</button>}
                {isHost && session?.status !== "COMPLETED" && <button
                    type="button" onClick={() => navigate(`/sessions/${sessionId}/invite`)}
                    className="rounded-full bg-white/80 px-4 py-2 text-sm font-semibold">Manage invitations</button>}
                {isHost && (
                    <button
                        type="button"
                        onClick={handleDeleteSessionHost}
                        className="flex items-center gap-2 rounded-full bg-white/80 px-4 py-2 text-[#c62828] border border-[#f3c1c5] shadow-sm hover:bg-white transition"
                        title="Delete Session"
                    >
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path d="M3 6h18" strokeWidth="2" strokeLinecap="round"/>
                            <path d="M8 6v-1a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v1" strokeWidth="2"/>
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" strokeWidth="2"/>
                            <path d="M10 11v6M14 11v6" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        <span className="text-sm font-semibold">Delete Session</span>
                    </button>
                )}
                <button
                    type="button"
                    onClick={handleCopyLink}
                    className="flex items-center gap-2 rounded-full bg-white/80 px-4 py-2 text-[#6f76a3] border border-[#e6e8f3] shadow-sm hover:bg-white transition"
                    title="Copy Link"
                >
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M10 13a5 5 0 0 0 7.07 0l2.83-2.83a5 5 0 0 0-7.07-7.07L10 4" strokeWidth="2"/>
                        <path d="M14 11a5 5 0 0 0-7.07 0L4.1 13.83a5 5 0 0 0 7.07 7.07L14 20" strokeWidth="2"/>
                    </svg>
                    <span className="text-sm font-semibold">{copied ? "Copied" : "Copy Link"}</span>
                </button>
            </div>

            <div className="flex h-full w-full items-center justify-center">
                {/* layout: cloud left — content — cloud right */}
                <div className="flex items-start justify-start gap-[41px]">
                    {/* floating cloud left */}
                    <motion.div
                        className="relative h-[111.914px] w-[150.711px] shrink-0"
                        animate={{y: [0, -8, 0]}}
                        transition={{duration: 9, repeat: Infinity, ease: "easeInOut", delay: 1}}
                    >
                        <div className="absolute bottom-[-30.37%] left-[-7.18%] right-[-7.36%] top-0">
                            <img className="block size-full max-w-none" src={CloudLeft} alt="cloud-left"/>
                        </div>
                    </motion.div>

                    {/* center column */}
                    <div className="flex w-[670px] flex-col items-center gap-[70px]">
                        {/* headline + subtitle */}
                        <motion.div
                            className="flex w-full flex-col items-center gap-[26px]"
                            initial={{opacity: 0, y: -40}}
                            animate={{opacity: 1, y: 0}}
                            transition={{duration: 0.3, delay: 0.2, ease: "easeOut"}}
                        >
                            <div
                                className="relative inline-grid grid-cols-[max-content] grid-rows-[max-content] place-items-start">
                                {/* underline decoration */}
                                <motion.div
                                    className="[grid-area:1_/_1] relative ml-[400px] mt-[52.5px] h-[5px] w-[170px]"
                                    initial={{opacity: 0, scaleX: 0}}
                                    animate={{opacity: 1, scaleX: 1}}
                                    transition={{duration: 0.4, delay: 0.6, ease: "easeOut"}}
                                >
                                    <div className="h-[5px] w-[170px] rotate-180 -scale-y-100">
                                        <div className="absolute inset-[-164.84%_-4.48%_-164.85%_-4.48%]">
                                            <DecorationUnderline/>
                                        </div>
                                    </div>
                                </motion.div>

                                {/* headline */}
                                <motion.h1
                                    className="[grid-area:1_/_1] m-0 font-['Outfit',_sans-serif] font-bold leading-normal text-[#232323] text-[48px] md:text-[54.947px] tracking-[-1.1px] whitespace-nowrap"
                                    initial={{opacity: 0, y: -50}}
                                    animate={{opacity: 1, y: 0}}
                                    transition={{duration: 0.3, ease: "easeOut"}}
                                >
                                    Waiting for your friends.
                                </motion.h1>
                            </div>

                            <motion.p
                                className="w-fit text-center font-['Outfit',_sans-serif] font-medium leading-normal text-[#858ca7] text-[16.667px] tracking-[-0.3667px]"
                                initial={{opacity: 0, y: -20}}
                                animate={{opacity: 1, y: 0}}
                                transition={{duration: 0.25, delay: 0.1, ease: "easeOut"}}
                            >
                                {me?.status === "JOINED" ? "Share your preferences to help your group find a trip." : `We’ll keep the group’s progress up to date${username ? `, ${username}` : ""}.`}
                            </motion.p>
                        </motion.div>

                        {/* grouped participants */}
                        <motion.div
                            className="w-full"
                            initial={{opacity: 0, y: -20}}
                            animate={{opacity: 1, y: 0}}
                            transition={{duration: 0.3, delay: 0.3, ease: "easeOut"}}
                        >
                            {showLoading && <GroupSkeleton variant="participants" label={computing ? "Passende Flüge für eure Gruppe werden gesucht" : "Teilnehmer werden geladen"}/>}
                            {error && <p className="text-center text-sm text-red-500">{error}</p>}

                            {!showLoading && !error && (
                                <>
                                    {/* Preferences completed = SUBMITTED */}
                                    <div className="mb-8">
                                        <p className="mb-3 text-sm font-semibold text-[#9aa0b8]">
                                            Preferences completed
                                        </p>
                                        {submittedList.length === 0 ? (
                                            <p className="text-sm text-gray-400">No one has submitted yet.</p>
                                        ) : (
                                            <ul className="flex flex-wrap justify-start gap-3">
                                                {submittedList.map((p, i) => {
                                                    const label = p.displayName || p.email || p.userId || "Unknown";
                                                    return (
                                                        <motion.li
                                                            key={`submitted-${label}-${i}`}
                                                            className={chipClasses("SUBMITTED")}
                                                            initial={{opacity: 0, y: 8}}
                                                            animate={{opacity: 1, y: 0}}
                                                            transition={{duration: 0.18, delay: 0.03 * i}}
                                                            aria-label="Preference Submitted"
                                                            title="Preference Submitted"
                                                        >
                                                            {label}
                                                        </motion.li>
                                                    );
                                                })}
                                            </ul>
                                        )}
                                    </div>

                                    {/* Not yet filled in preferences = JOINED + INVITED (INVITED more transparent) */}
                                    <div>
                                        <p className="mb-3 text-sm font-semibold text-[#9aa0b8]">
                                            Not yet filled in preferences
                                        </p>
                                        {notFilledList.length === 0 ? (
                                            <p className="text-sm text-gray-400">Everyone is done 🎉</p>
                                        ) : (
                                            <ul className="flex flex-wrap justify-start gap-3">
                                                {notFilledList.map((p, i) => {
                                                    const label = p.displayName || p.email || p.userId || "Unknown";
                                                    return (
                                                        <motion.li
                                                            key={`notfilled-${label}-${i}`}
                                                            className={chipClasses(p.status)}
                                                            initial={{opacity: 0, y: 8}}
                                                            animate={{opacity: 1, y: 0}}
                                                            transition={{duration: 0.18, delay: 0.03 * i}}
                                                            aria-label={p.status === "INVITED" ? "Invited" : "Joined"}
                                                            title={p.status === "INVITED" ? "Invited" : "Joined"}
                                                        >
                                                            {label}
                                                        </motion.li>
                                                    );
                                                })}
                                            </ul>
                                        )}
                                    </div>
                                </>
                            )}
                        </motion.div>

                        {/* bottom-right CTA */}
                        <div className="w-full flex justify-end">
                            <motion.button
                                type="button"
                                disabled={ctaDisabled}
                                aria-disabled={ctaDisabled}
                                onClick={ctaDisabled ? undefined : ctaOnClick}
                                className={[
                                    "box-border flex shrink-0 items-center justify-center gap-3 rounded-[26.25px] px-10 py-3.5 transition",
                                    "bg-[#b282e5] text-white",
                                    ctaDisabled
                                        ? "cursor-not-allowed"
                                        : "cursor-pointer hover:shadow-[0_8px_25px_rgba(178,130,229,0.4)]",
                                ].join(" ")}
                                whileHover={ctaDisabled ? undefined : {scale: 1.05}}
                                whileTap={ctaDisabled ? undefined : {scale: 0.98}}
                            >
                                {ctaLabel === "Start the search" && <SearchIcon/>}
                                <span
                                    className="whitespace-nowrap font-['Space_Grotesk',_sans-serif] text-[15.978px] font-bold tracking-[-0.8788px] leading-normal">
                  {ctaLabel}
                </span>
                            </motion.button>
                        </div>
                    </div>

                    {/* floating cloud right */}
                    <motion.div
                        className="relative h-[100.182px] w-[134.912px] shrink-0"
                        animate={{y: [0, 10, 0]}}
                        transition={{duration: 8, repeat: Infinity, ease: "easeInOut", delay: 2}}
                    >
                        <div className="absolute bottom-[-26.67%] left-[-4.42%] right-[-4.61%] top-0">
                            <img className="block size-full max-w-none" src={CloudRight} alt="cloud-right"/>
                        </div>
                    </motion.div>
                </div>
            </div>
        </motion.div>
    );
}
